// POS route cədvəli — front-un cloud path-larını pos_local /v1 əməliyyatlarına bağlayır.
//
// Hər route: cloud method+pathname-i tutur, pos_local-a sorğu atır, cavabı cloud
// shape-inə çevirib qaytarır. Tutulmayan route-lar interceptor tərəfindən toxunulmur
// və cloud-a gedir (hibrid davranış).

import { posRequest } from './posClient';
import { posTablesListToCloud, posTableGroupsToCloud, PosTable } from './mappers';
import {
  posProductToCloud,
  posCategoryToCloud,
  parseI18nName,
  PosProduct,
  PosCategory,
} from './catalogMappers';
import { posOrderToCloudTableOrder, PosOrder } from './orderMappers';
import { runStaffLogin, StaffLoginBody } from './authGateway';
import {
  runCloseBill,
  listPrepayments,
  addPrepayment,
  deletePrepayment,
  CloseBillBody,
  AddPrepaymentBody,
} from './paymentGateway';
import { getDailyReport, runEndOfDay } from './shiftGateway';
import { scheduleSendToKitchen } from './kitchenPrint';

export interface PosRouteContext {
  method: string;
  pathname: string;
  query: Record<string, string>;
  body: unknown;
}

export interface PosRouteResult {
  status: number;
  data: unknown; // front-un response.data = bu olacaq (cloud shape)
}

export interface PosRoute {
  /** Bu route sorğunu idarə edirmi? */
  match(ctx: PosRouteContext): boolean;
  /** İdarə et: pos_local-a sorğu at, cloud-shape data qaytar. */
  run(ctx: PosRouteContext): Promise<PosRouteResult>;
}

/** pathname-in sonu verilən suffikslə bitir (query-siz). */
function endsWith(pathname: string, suffix: string): boolean {
  const p = pathname.replace(/\/+$/, '');
  return p === suffix || p.endsWith(suffix);
}

/** "/api/tables/tbl_5/order" + "order" → "tbl_5"; uyğun deyilsə null. */
function tableSubPath(pathname: string, suffix: string): string | null {
  const p = pathname.replace(/\/+$/, '');
  const m = p.match(new RegExp(`/tables/([^/]+)/${suffix}$`));
  return m ? decodeURIComponent(m[1]) : null;
}

/** "/order/ord_5/payments" və ya "/orders/ord_5/payments" → "ord_5".
 *  Front-da həm tək (`/order/`, HesapKes) həm cəm (`/orders/`, GunlukKasa/HesapKesSip)
 *  forma işlənir — ikisini də tuturuq. `payments` suffiksi `prepayments`-ə uyğun gəlmir
 *  (öndəki `/` literal-dır). */
function orderSubPath(pathname: string, suffix: string): string | null {
  const p = pathname.replace(/\/+$/, '');
  const m = p.match(new RegExp(`/orders?/([^/]+)/${suffix}$`));
  return m ? decodeURIComponent(m[1]) : null;
}

/** pos_local `/v1/kitchens` cavabı. */
interface PosKitchen {
  id: string;
  name: string;
  printerId: string | null;
  color: string | null;
  sortOrder: number;
}

/**
 * pos_local mətbəxi → cloud shape (`getKitchens()` numerik id gözləyir).
 * `has_printer` false olan mətbəx çap edə bilmir — front bunu göstərir.
 */
function posKitchenToCloud(k: PosKitchen): {
  id: number;
  name: string;
  color: string | null;
  has_printer: boolean;
} {
  return {
    id: Number(String(k.id).replace(/^kit_/, '')) || 0,
    name: k.name,
    color: k.color,
    has_printer: Boolean(k.printerId),
  };
}

/** Cloud/UI-dan gələn mətbəx id-si → pos_local formatı (`7` → `kit_7`). */
function toPosKitchenId(raw: string | number): string {
  const s = String(raw);
  return /^kit_/.test(s) ? s : `kit_${s}`;
}

/** pos_local mətbəx ekranı cavabı (yalnız lazım olan sahələr). */
interface KitchenDisplayPayload {
  kitchen: unknown;
  tickets?: Array<{ items?: Array<{ name: string }> } & Record<string, unknown>>;
}

/** "/kitchens/3/display" → "3"; uyğun deyilsə null. */
function kitchenDisplayId(pathname: string): string | null {
  const m = pathname.replace(/\/+$/, '').match(/\/kitchens\/([^/]+)\/display$/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** "/kitchens/3/archive" → "3"; uyğun deyilsə null. */
function kitchenArchiveId(pathname: string): string | null {
  const m = pathname.replace(/\/+$/, '').match(/\/kitchens\/([^/]+)\/archive$/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** "/kitchens/3/orders/ord_9/status" → { kitchenId, orderId }; uyğun deyilsə null. */
function kitchenOrderStatusRef(
  pathname: string,
): { kitchenId: string; orderId: string } | null {
  const m = pathname
    .replace(/\/+$/, '')
    .match(/\/kitchens\/([^/]+)\/orders\/([^/]+)\/status$/);
  return m
    ? { kitchenId: decodeURIComponent(m[1]), orderId: decodeURIComponent(m[2]) }
    : null;
}

/** POS rejimində masa id-ləri onsuz da pos_local formatındadır (`tbl_5`) — cloud
 *  shape-i `mappers.ts`-də `id: t.id` ilə qurulur. Numerik id gəlsə (köhnə cloud
 *  datası / manual çağırış) prefiksləyirik. */
function toPosTableId(raw: string): string {
  return /^tbl_/.test(raw) ? raw : `tbl_${raw}`;
}

/** "/order(s)/ord_5/prepayments/pay_9" → { orderId, prepareId }; uyğun deyilsə null. */
function prepaymentRef(pathname: string): { orderId: string; prepareId: string } | null {
  const p = pathname.replace(/\/+$/, '');
  const m = p.match(/\/orders?\/([^/]+)\/prepayments\/([^/]+)$/);
  return m
    ? { orderId: decodeURIComponent(m[1]), prepareId: decodeURIComponent(m[2]) }
    : null;
}

/** "/stocks/42" → "42"; "/stocks", "/stocks-simple", "/stock-sets/5" üçün null. */
function stockDetailId(pathname: string): string | null {
  const p = pathname.replace(/\/+$/, '');
  const m = p.match(/\/stocks\/([^/]+)$/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** "/stock-groups/4" → "4"; "/stock-groups" (list) üçün null. */
function stockGroupDetailId(pathname: string): string | null {
  const p = pathname.replace(/\/+$/, '');
  const m = p.match(/\/stock-groups\/([^/]+)$/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** Verilən masanın açıq sifariş cəmlərini (cents) çək — masa grid tutarı üçün. */
async function fetchOrderTotals(tables: PosTable[]): Promise<Record<string, number>> {
  const totals: Record<string, number> = {};
  await Promise.all(
    tables
      .filter((t) => t.currentOrderId)
      .map(async (t) => {
        const o = await posRequest<PosOrder>({
          method: 'GET',
          path: `/v1/orders/${t.currentOrderId}`,
        });
        if (o.ok && o.data) totals[t.id] = o.data.totals?.totalCents ?? 0;
      }),
  );
  return totals;
}

/** Masanı id ilə tap (currentOrderId + label üçün). */
async function getTable(tableId: string): Promise<PosTable | null> {
  const res = await posRequest<PosTable[]>({ method: 'GET', path: '/v1/tables' });
  if (!res.ok) throw makePosError(res);
  return (res.data ?? []).find((t) => t.id === tableId) ?? null;
}

export const POS_ROUTES: PosRoute[] = [
  // ─── POST /auth/staff-login → cloud doğrula + pos_local provision ──────────
  // restaurant-login (email/parol) TOXUNULMUR — cloud-da qalır (device_token +
  // staff list verir). Yalnız PIN/staff-login hibrid bootstrap-dan keçir.
  {
    match: (c) => c.method === 'POST' && endsWith(c.pathname, '/auth/staff-login'),
    run: (ctx) => runStaffLogin((ctx.body ?? {}) as StaffLoginBody),
  },

  // ─── GET /tables → pos_local GET /v1/tables (+ bölmə filtri + tutar) ───────
  {
    match: (c) => c.method === 'GET' && endsWith(c.pathname, '/tables'),
    run: async (ctx) => {
      const res = await posRequest<PosTable[]>({ method: 'GET', path: '/v1/tables' });
      if (!res.ok) throw makePosError(res);
      const tables = res.data ?? [];
      const totals = await fetchOrderTotals(tables);
      // Section tab kliklənəndə front table_group_id göndərir → həmin bölməni süz.
      const raw = ctx.query.table_group_id;
      const n = raw !== undefined && raw !== '' ? Number(raw) : null;
      const filter = n != null && Number.isNaN(n) ? null : n;
      return { status: 200, data: posTablesListToCloud(tables, filter, totals) };
    },
  },

  // ─── GET /table-groups → label-dan törədilmiş bölmələr (Salon/Teras/VIP) ────
  {
    match: (c) => c.method === 'GET' && endsWith(c.pathname, '/table-groups'),
    run: async () => {
      const res = await posRequest<PosTable[]>({ method: 'GET', path: '/v1/tables' });
      if (!res.ok) throw makePosError(res);
      return { status: 200, data: posTableGroupsToCloud(res.data ?? []) };
    },
  },

  // ─── GET /table-categories → istifadə olunmur, boş ─────────────────────────
  {
    match: (c) => c.method === 'GET' && endsWith(c.pathname, '/table-categories'),
    run: async () => ({ status: 200, data: [] }),
  },

  // ─── CATALOG (menyu) → pos_local /v1/catalog/* ─────────────────────────────
  // GET /stock-groups → kateqoriyalar
  {
    match: (c) => c.method === 'GET' && endsWith(c.pathname, '/stock-groups'),
    run: async () => {
      const res = await posRequest<PosCategory[]>({ method: 'GET', path: '/v1/catalog/categories' });
      if (!res.ok) throw makePosError(res);
      return { status: 200, data: (res.data ?? []).map(posCategoryToCloud) };
    },
  },
  // GET /stocks və /stocks-simple → məhsullar
  {
    match: (c) =>
      c.method === 'GET' &&
      (endsWith(c.pathname, '/stocks') || endsWith(c.pathname, '/stocks-simple')),
    run: async () => {
      const res = await posRequest<PosProduct[]>({ method: 'GET', path: '/v1/catalog/products' });
      if (!res.ok) throw makePosError(res);
      return { status: 200, data: (res.data ?? []).map(posProductToCloud) };
    },
  },
  // GET /stock-sets → pos_local-da set yoxdur, boş
  {
    match: (c) => c.method === 'GET' && endsWith(c.pathname, '/stock-sets'),
    run: async () => ({ status: 200, data: [] }),
  },
  // GET /kitchens/:id/display → mətbəx ekranı.
  // POS rejimində sifarişlər pos_local-dadır; cloud KDS onları görmür, ona görə
  // ticket-ləri birbaşa yerli bazadan veririk (internetsiz də işləyir).
  {
    match: (c) => c.method === 'GET' && kitchenDisplayId(c.pathname) !== null,
    run: async (ctx) => {
      const id = kitchenDisplayId(ctx.pathname)!;
      const res = await posRequest<KitchenDisplayPayload>({
        method: 'GET',
        path: `/v1/kitchens/${toPosKitchenId(id)}/display`,
      });
      if (!res.ok) throw makePosError(res);
      // Məhsul adları cloud-dan çoxdilli JSON kimi gəlir (`{"az":"Revani"}`) —
      // mətbəx fişində xam JSON görünməsin.
      const payload = res.data ?? { kitchen: null, tickets: [] };
      return {
        status: 200,
        data: {
          ...payload,
          tickets: (payload.tickets ?? []).map((ticket) => ({
            ...ticket,
            items: (ticket.items ?? []).map((item) => ({
              ...item,
              name: parseI18nName(item.name),
            })),
          })),
        },
      };
    },
  },
  // GET /kitchens/:id/archive → təhvil alınmış ticket-lər (tarix aralığı).
  {
    match: (c) => c.method === 'GET' && kitchenArchiveId(c.pathname) !== null,
    run: async (ctx) => {
      const id = kitchenArchiveId(ctx.pathname)!;
      const qs = new URLSearchParams();
      if (ctx.query.from) qs.set('from', ctx.query.from);
      if (ctx.query.to) qs.set('to', ctx.query.to);
      if (ctx.query.limit) qs.set('limit', ctx.query.limit);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';

      const res = await posRequest<KitchenDisplayPayload>({
        method: 'GET',
        path: `/v1/kitchens/${toPosKitchenId(id)}/archive${suffix}`,
      });
      if (!res.ok) throw makePosError(res);
      const payload = res.data ?? { kitchen: null, tickets: [] };
      return {
        status: 200,
        data: {
          ...payload,
          tickets: (payload.tickets ?? []).map((ticket) => ({
            ...ticket,
            items: (ticket.items ?? []).map((item) => ({
              ...item,
              name: parseI18nName(item.name),
            })),
          })),
        },
      };
    },
  },
  // PATCH /kitchens/:id/orders/:orderId/status → sətir statusunu dəyiş.
  {
    match: (c) => c.method === 'PATCH' && kitchenOrderStatusRef(c.pathname) !== null,
    run: async (ctx) => {
      const ref = kitchenOrderStatusRef(ctx.pathname)!;
      const body = (ctx.body ?? {}) as { status?: string };
      const res = await posRequest<unknown>({
        method: 'PATCH',
        path: `/v1/kitchens/${toPosKitchenId(ref.kitchenId)}/orders/${ref.orderId}/status`,
        body: { status: body.status },
      });
      if (!res.ok) throw makePosError(res);
      return { status: 200, data: res.data };
    },
  },
  // GET /kitchens → mətbəxlər (ofisantın "hansı mətbəxə göndərim?" seçimi).
  // Cloud shape-i numerik id gözləyir, pos_local isə `kit_7` verir — çeviririk.
  {
    match: (c) => c.method === 'GET' && endsWith(c.pathname, '/kitchens'),
    run: async () => {
      const res = await posRequest<PosKitchen[]>({ method: 'GET', path: '/v1/kitchens' });
      if (!res.ok) throw makePosError(res);
      return { status: 200, data: (res.data ?? []).map(posKitchenToCloud) };
    },
  },
  // GET /stocks/:id → tək məhsul detalı (pos_local /v1/catalog/products/prd_:id)
  {
    match: (c) => c.method === 'GET' && stockDetailId(c.pathname) !== null,
    run: async (ctx) => {
      const id = stockDetailId(ctx.pathname)!;
      const res = await posRequest<PosProduct>({
        method: 'GET',
        path: `/v1/catalog/products/prd_${id}`,
      });
      if (!res.ok) throw makePosError(res);
      return { status: 200, data: res.data ? posProductToCloud(res.data) : null };
    },
  },
  // GET /stock-groups/:id → kateqoriya detalı + içindəki məhsullar (front şəkil üçün
  // `detail.stocks` istifadə edir). pos_local-da kateqoriya-detalı endpoint-i yoxdur →
  // categories + products?categoryId-dən sintez edirik (cloud-a düşüb 403 olmasın).
  {
    match: (c) => c.method === 'GET' && stockGroupDetailId(c.pathname) !== null,
    run: async (ctx) => {
      const id = stockGroupDetailId(ctx.pathname)!;
      const [cats, prods] = await Promise.all([
        posRequest<PosCategory[]>({ method: 'GET', path: '/v1/catalog/categories' }),
        posRequest<PosProduct[]>({
          method: 'GET',
          path: '/v1/catalog/products',
          query: { categoryId: `cat_${id}` },
        }),
      ]);
      const cat = (cats.data ?? []).find((c) => c.id === `cat_${id}`);
      const base = cat ? posCategoryToCloud(cat) : { id: Number(id), name: '' };
      const stocks = (prods.data ?? []).map(posProductToCloud);
      return { status: 200, data: { ...base, stocks } };
    },
  },

  // ─── ORDER (masa-mərkəzli) → pos_local /v1/orders/* ────────────────────────
  // GET /tables/:id/order → masanın açıq sifarişi
  {
    match: (c) => c.method === 'GET' && tableSubPath(c.pathname, 'order') !== null,
    run: async (ctx) => {
      const tableId = tableSubPath(ctx.pathname, 'order')!;
      const table = await getTable(tableId);
      const tableName = table?.label ?? '';
      if (!table?.currentOrderId) {
        return { status: 200, data: posOrderToCloudTableOrder(tableName, null) };
      }
      const o = await posRequest<PosOrder>({
        method: 'GET',
        path: `/v1/orders/${table.currentOrderId}`,
      });
      return {
        status: 200,
        data: posOrderToCloudTableOrder(tableName, o.ok ? o.data ?? null : null),
      };
    },
  },

  // POST /tables/:id/add-stock → açıq sifariş yarat (lazımsa) + məhsul əlavə et
  {
    match: (c) => c.method === 'POST' && tableSubPath(c.pathname, 'add-stock') !== null,
    run: async (ctx) => {
      const tableId = tableSubPath(ctx.pathname, 'add-stock')!;
      const body = (ctx.body ?? {}) as {
        stock_id?: number | string;
        quantity?: number;
        count?: number;
        notes?: string;
        note?: string;
        kitchen_id?: string | number | null;
        kitchenId?: string | number | null;
        modifier_ids?: unknown;
        modifierIds?: unknown;
      };
      const stockId = body.stock_id;
      const qty = Number(body.quantity ?? body.count ?? 1);
      // Ofisantın seçdiyi mətbəx. Boşdursa pos_local avtomatik marşrutlaşdırır.
      const rawKitchen = body.kitchen_id ?? body.kitchenId;
      const kitchenId =
        rawKitchen === null || rawKitchen === undefined || rawKitchen === ''
          ? null
          : toPosKitchenId(rawKitchen);
      // Mətbəx qeydi (Acılı, Buzsuz...) — pos_local item-də notes saxlayır və KOT-da çap edir.
      const rawNote = body.notes ?? body.note;
      const note = typeof rawNote === 'string' ? rawNote.trim() : '';

      // Ofisantın seçdiyi opsiyalar (ölçü, əlavə tərkib). pos_local id-ləri
      // OLDUĞU KİMİ ötürülür — çevirmə yoxdur. Qayda yoxlaması (min/max, opsiya
      // bu məhsula aiddirmi) pos_local-da aparılır; bura yalnız nəqliyyatdır.
      const rawModifiers = body.modifier_ids ?? body.modifierIds;
      const modifierIds = Array.isArray(rawModifiers)
        ? rawModifiers
            .filter((m) => typeof m === 'string' || typeof m === 'number')
            .map((m) => String(m).trim())
            .filter((m) => m !== '')
        : [];

      let table = await getTable(tableId);
      let orderId = table?.currentOrderId ?? null;
      if (!orderId) {
        const open = await posRequest<PosOrder>({
          method: 'POST',
          path: '/v1/orders',
          body: { type: 'dine_in', tableId },
        });
        if (!open.ok) throw makePosError(open);
        orderId = open.data!.id;
      }
      const add = await posRequest<PosOrder>({
        method: 'POST',
        path: `/v1/orders/${orderId}/items`,
        body: {
          productId: `prd_${stockId}`,
          quantity: qty,
          ...(note ? { notes: note } : {}),
          ...(modifierIds.length > 0 ? { modifierIds } : {}),
        },
      });
      if (!add.ok) throw makePosError(add);

      // KOT çapı: bu "Mutfak" klikləməsinin add-stock burst-unu birləşdirib
      // tək send-to-kitchen çağırışı planlaşdır (debounce). Çap fon işidir,
      // response-u bloklamır. Bax: kitchenPrint.ts.
      scheduleSendToKitchen(orderId, kitchenId);

      // totals zəmanətli olsun deyə order-i GET ilə təzə çək
      table = await getTable(tableId);
      const finalOrder = await posRequest<PosOrder>({
        method: 'GET',
        path: `/v1/orders/${orderId}`,
      });
      return {
        status: 200,
        data: posOrderToCloudTableOrder(
          table?.label ?? '',
          finalOrder.ok ? finalOrder.data ?? null : null,
        ),
      };
    },
  },

  // POST /tables/:id/subtract-stock → sətir miqdarını ± dəyiş (front +/- düymələri)
  // body: { stock_id, quantity, pivotId, increase }. pivotId = pos order item id.
  // increase=false → azalt (0-a düşsə sətir silinir); increase=true → artır.
  {
    match: (c) => c.method === 'POST' && tableSubPath(c.pathname, 'subtract-stock') !== null,
    run: async (ctx) => {
      const tableId = tableSubPath(ctx.pathname, 'subtract-stock')!;
      const body = (ctx.body ?? {}) as {
        pivotId?: string;
        quantity?: number;
        increase?: boolean;
        approval_token?: string;
      };
      const pivotId = String(body.pivotId ?? '');
      // Menecer təsdiqi tokeni. Sətir mətbəxə göndərildikdən sonra silinirsə
      // pos_local onu TƏLƏB EDİR (orders.voidItem.afterKitchen).
      const approvalToken = typeof body.approval_token === 'string' ? body.approval_token : '';
      const delta = Number(body.quantity ?? 1);
      const increase = body.increase === true;

      const table = await getTable(tableId);
      const orderId = table?.currentOrderId ?? null;
      if (!orderId) {
        throw makePosError({ error: { code: 'NO_OPEN_ORDER', message: 'Masada açıq sifariş yoxdur' } });
      }

      const o = await posRequest<PosOrder>({ method: 'GET', path: `/v1/orders/${orderId}` });
      if (!o.ok || !o.data) throw makePosError(o);
      const item = (o.data.items ?? []).find((i) => i.id === pivotId && !i.isVoided);
      if (!item) {
        throw makePosError({ error: { code: 'ITEM_NOT_FOUND', message: 'Sifariş sətri tapılmadı' } });
      }

      const newQty = increase ? item.quantity + delta : item.quantity - delta;
      if (newQty <= 0) {
        const del = await posRequest({
          method: 'DELETE',
          path: `/v1/orders/${orderId}/items/${pivotId}`,
          body: { reason: 'removed', ...(approvalToken ? { approvalToken } : {}) },
        });
        if (!del.ok) throw makePosError(del);
      } else {
        const patch = await posRequest({
          method: 'PATCH',
          path: `/v1/orders/${orderId}/items/${pivotId}`,
          body: { quantity: newQty },
        });
        if (!patch.ok) throw makePosError(patch);
      }

      const finalOrder = await posRequest<PosOrder>({ method: 'GET', path: `/v1/orders/${orderId}` });
      return {
        status: 200,
        data: posOrderToCloudTableOrder(
          table?.label ?? '',
          finalOrder.ok ? finalOrder.data ?? null : null,
        ),
      };
    },
  },

  // POST /tables/:id/change-table  body: { table_id: <hədəf> }
  // Cloud "Masa dəyişdir" modalı (MasaDegistir.tsx) → pos_local change-table.
  // POS rejimində bu route olmasaydı sorğu cloud-a gedərdi və lokal sifariş
  // masada qalıb desync yaradardı.
  {
    match: (c) => c.method === 'POST' && tableSubPath(c.pathname, 'change-table') !== null,
    run: async (ctx) => {
      const fromTableId = tableSubPath(ctx.pathname, 'change-table')!;
      const body = (ctx.body ?? {}) as { table_id?: string | number };
      const toTableId = String(body.table_id ?? '');
      if (!toTableId) {
        throw makePosError({ error: { code: 'INVALID_INPUT', message: 'Hədəf masa seçilməyib' } });
      }
      const res = await posRequest({
        method: 'POST',
        path: `/v1/tables/${fromTableId}/change-table`,
        body: { toTableId: toPosTableId(toTableId) },
      });
      if (!res.ok) throw makePosError(res);
      return { status: 200, data: { message: 'Masa dəyişdirildi' } };
    },
  },

  // POST /tables/:id/merge-table  body: { table_id: <hədəf> }
  // Cloud "Masaları birləşdir" modalı (MasaBirlesdir.tsx) → pos_local merge-table.
  {
    match: (c) => c.method === 'POST' && tableSubPath(c.pathname, 'merge-table') !== null,
    run: async (ctx) => {
      const sourceTableId = tableSubPath(ctx.pathname, 'merge-table')!;
      const body = (ctx.body ?? {}) as { table_id?: string | number };
      const targetTableId = String(body.table_id ?? '');
      if (!targetTableId) {
        throw makePosError({ error: { code: 'INVALID_INPUT', message: 'Hədəf masa seçilməyib' } });
      }
      const res = await posRequest({
        method: 'POST',
        path: `/v1/tables/${sourceTableId}/merge-table`,
        body: { toTableId: toPosTableId(targetTableId) },
      });
      if (!res.ok) throw makePosError(res);
      return { status: 200, data: { message: 'Masalar birləşdirildi' } };
    },
  },

  // DELETE /tables/:id/cancel-order → masanı təmizlə:
  //   • açıq sifariş varsa → cancel (pos_local ləğvi masanı avtomatik azad edir);
  //   • açıq sifariş yoxdur, masa "dirty" (ödəniş bitib) → status → free.
  {
    match: (c) => c.method === 'DELETE' && tableSubPath(c.pathname, 'cancel-order') !== null,
    run: async (ctx) => {
      const tableId = tableSubPath(ctx.pathname, 'cancel-order')!;
      const table = await getTable(tableId);
      const orderId = table?.currentOrderId ?? null;
      if (orderId) {
        const res = await posRequest({
          method: 'POST',
          path: `/v1/orders/${orderId}/cancel`,
          body: { reason: 'cancelled' },
        });
        if (!res.ok) throw makePosError(res);
      } else if (table?.status === 'dirty') {
        const res = await posRequest({
          method: 'POST',
          path: `/v1/tables/${tableId}/status`,
          body: { toStatus: 'free' },
        });
        if (!res.ok) throw makePosError(res);
      }
      return { status: 200, data: { message: 'Masa təmizləndi' } };
    },
  },

  // ─── PAYMENTS → pos_local /v1/orders/:id/payments + /close ─────────────────
  // DELETE /order/:id/prepayments/:prepareId → ön ödənişi void et
  // (POST /payments-dən ÖNCƏ olmalıdır ki, prepayments path-ı düzgün tutulsun).
  {
    match: (c) => c.method === 'DELETE' && prepaymentRef(c.pathname) !== null,
    run: async (ctx) => {
      const ref = prepaymentRef(ctx.pathname)!;
      return deletePrepayment(ref.orderId, ref.prepareId);
    },
  },
  // GET /order/:id/prepayments → ön ödənişlərin siyahısı
  {
    match: (c) => c.method === 'GET' && orderSubPath(c.pathname, 'prepayments') !== null,
    run: (ctx) => listPrepayments(orderSubPath(ctx.pathname, 'prepayments')!),
  },
  // POST /order/:id/prepayments → qismən (ön) ödəniş, order açıq qalır
  {
    match: (c) => c.method === 'POST' && orderSubPath(c.pathname, 'prepayments') !== null,
    run: (ctx) =>
      addPrepayment(
        orderSubPath(ctx.pathname, 'prepayments')!,
        (ctx.body ?? {}) as AddPrepaymentBody,
      ),
  },
  // POST /order/:id/payments → hesabı bağla (ödəniş + close)
  {
    match: (c) => c.method === 'POST' && orderSubPath(c.pathname, 'payments') !== null,
    run: (ctx) =>
      runCloseBill(
        orderSubPath(ctx.pathname, 'payments')!,
        (ctx.body ?? {}) as CloseBillBody,
      ),
  },

  // ─── SHIFT (günlük kassa) → pos_local /v1/shifts/* ─────────────────────────
  // POST /payments/end-of-day → cari növbəni bağla (gün sonu)
  // (GET /payments-dən fərqli method; /payments/pos/sale & /online/create TOXUNULMUR.)
  {
    match: (c) => c.method === 'POST' && endsWith(c.pathname, '/payments/end-of-day'),
    run: () => runEndOfDay(),
  },
  // GET /report → kassa hesabatı. Front tarix filtrini `from`/`to` ilə göndərir
  // (fallback `/payments` isə `open_date`/`close_date` işlədir) — ikisini də oxuyuruq,
  // əks halda filtr sakitcə nəzərə alınmırdı.
  {
    match: (c) => c.method === 'GET' && endsWith(c.pathname, '/report'),
    run: (ctx) => getDailyReport(rangeFromQuery(ctx.query)),
  },
  // GET /payments → report fallback-ı
  {
    match: (c) => c.method === 'GET' && endsWith(c.pathname, '/payments'),
    run: (ctx) => getDailyReport(rangeFromQuery(ctx.query)),
  },
];

/** Query-dən tarix aralığını oxu — `from`/`to` (report) və `open_date`/`close_date` (payments). */
function rangeFromQuery(q: Record<string, unknown> | undefined): { from?: string; to?: string } {
  const pick = (...keys: string[]): string | undefined => {
    for (const k of keys) {
      const v = q?.[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return undefined;
  };
  const from = pick('from', 'open_date', 'start_date');
  const to = pick('to', 'close_date', 'end_date');
  return { ...(from ? { from } : {}), ...(to ? { to } : {}) };
}

/** pos_local {ok:false} cavabını axios-uyğun error-a çevirmək üçün. */
export function makePosError(res: { error?: { code: string; message: string } }) {
  const status = (res as { _status?: number })._status ?? 400;
  const message = res.error?.message || 'POS xətası';
  const code = res.error?.code || 'POS_ERROR';
  const err = new Error(message) as Error & { posCode?: string; posStatus?: number };
  err.posCode = code;
  err.posStatus = status;
  return err;
}

/** Verilən sorğu üçün uyğun POS route-u tap (yoxdursa null). */
export function matchPosRoute(ctx: PosRouteContext): PosRoute | null {
  return POS_ROUTES.find((r) => r.match(ctx)) ?? null;
}
